import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  AdminAnalyticsView,
  AdminErrorsView,
  AdminProductView,
  AdminSpendTable,
  AdminStoriesView,
  fmtDateTime,
  fmtInt,
  fmtRub,
  useAdminAnalyticsQuery,
  useAdminDashboardQuery,
  useAdminErrorsQuery,
  useAdminMetrikaQuery,
  useAdminProductQuery,
  useAdminStoriesQuery,
  useAdminElevenlabsQuery,
  type AdminPart,
  type AnalyticsPeriod,
} from '@features/Admin';
import { cn } from '@shared/lib';
import { Card } from '@shared/ui';

const PERIODS: AnalyticsPeriod[] = [7, 30, 90];
const PERIOD_KEY = 'lx.admin.period';
const TAB_KEY = 'lx.admin.tab';
const ERRORS_ID = 'admin-errors';
const TABS: AdminPart[] = ['product', 'tech'];

function storedTab(): AdminPart {
  try {
    return localStorage.getItem(TAB_KEY) === 'tech' ? 'tech' : 'product';
  } catch {
    return 'product';
  }
}

function storedPeriod(): AnalyticsPeriod {
  try {
    const value = Number(localStorage.getItem(PERIOD_KEY));
    return PERIODS.includes(value as AnalyticsPeriod) ? (value as AnalyticsPeriod) : 30;
  } catch {
    return 30;
  }
}

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const [days, setDays] = useState<AnalyticsPeriod>(storedPeriod);
  // Две вкладки (решение владельца 2026-10-21): «Аналитика» — ученики и удержание, «Техника» — траты и ошибки.
  const [tab, setTab] = useState<AdminPart>(storedTab);
  const analytics = useAdminAnalyticsQuery(days);
  const product = useAdminProductQuery(days, tab === 'product');
  const metrika = useAdminMetrikaQuery(days, tab === 'product');
  const stories = useAdminStoriesQuery(days, tab === 'product');
  const eleven = useAdminElevenlabsQuery(tab === 'product');
  const errors = useAdminErrorsQuery(days);
  const query = useAdminDashboardQuery();

  // 403 = роль не ADMIN. Отделяем от прочих ошибок, чтобы показать понятное
  // «нет доступа», а не «обнови страницу».
  const failure = analytics.error ?? query.error ?? product.error;
  const status = isAxiosError(failure) ? failure.response?.status : undefined;
  const noAccess = status === 403;
  const chooseTab = (value: AdminPart) => {
    setTab(value);
    try {
      localStorage.setItem(TAB_KEY, value);
    } catch {
      /* вкладка — удобство */
    }
  };
  const choose = (value: AnalyticsPeriod) => {
    setDays(value);
    try {
      localStorage.setItem(PERIOD_KEY, String(value));
    } catch {
      /* период — удобство, без хранилища просто не запомнится */
    }
  };

  // amp-block / ym-hide-content: админка с именами, email и расходами всех пользователей
  // не попадает в записи сессий Amplitude и Вебвизора.
  return (
    <div className="amp-block ym-hide-content mx-auto max-w-[1400px] animate-fadeIn px-4 pb-28 pt-16 sm:px-6 nav:px-12 nav:pb-20 nav:pt-10">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-balance font-serif text-[30px] font-medium leading-[1.2] tracking-[-0.02em] sm:text-[36px]">
            {t('admin.analytics.title')}
          </h1>
          <div className="mt-1.5 text-[14px] text-muted">
            {t('admin.analytics.sub')}
            {analytics.data && <> · {t('admin.generatedAt', { time: fmtDateTime(analytics.data.generatedAt, lang) })}</>}
            {errors.data?.since && (
              <>
                {' · '}
                <button
                  type="button"
                  onClick={() => {
                    chooseTab('tech');
                    window.setTimeout(
                      () => document.getElementById(ERRORS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                      0,
                    );
                  }}
                  className={cn('underline-offset-2 hover:underline', errors.data.today > 0 && 'text-[#f47a7e]')}
                >
                  {t('admin.errors.todayLink', { n: fmtInt(errors.data.today) })}
                </button>
              </>
            )}
          </div>
        </div>
        {!noAccess && (
          <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 rounded-[12px] bg-card p-1 shadow-[inset_0_0_0_1px_#2a2a30]" role="tablist" aria-label={t('admin.tabs.label')}>
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => chooseTab(value)}
                className={cn(
                  'h-8 rounded-[9px] px-3.5 text-[14px] font-medium transition-colors',
                  tab === value ? 'bg-elev text-fg shadow-[inset_0_0_0_1px_#3a3a40]' : 'text-muted hover:text-fg',
                )}
              >
                {t(`admin.tabs.${value}`)}
              </button>
            ))}
          </div>
          <div className="inline-flex gap-1 rounded-[12px] bg-card p-1 shadow-[inset_0_0_0_1px_#2a2a30]" role="radiogroup" aria-label={t('admin.analytics.periodLabel')}>
            {PERIODS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={days === value}
                onClick={() => choose(value)}
                className={cn(
                  'h-8 rounded-[9px] px-3.5 text-[14px] font-medium tabular-nums transition-colors',
                  days === value ? 'bg-elev text-fg shadow-[inset_0_0_0_1px_#3a3a40]' : 'text-muted hover:text-fg',
                )}
              >
                {t('admin.analytics.periodOption', { days: value })}
              </button>
            ))}
          </div>
          </div>
        )}
      </header>

      {noAccess ? (
        <Card padding="lg">
          <div className="font-serif text-[20px] font-medium tracking-[-0.01em]">{t('admin.noAccess.title')}</div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-muted">{t('admin.noAccess.body')}</p>
        </Card>
      ) : (
        tab === 'product' ? (
        <div className="space-y-8">
          {product.isPending ? (
            <div className="h-[520px] animate-skeleton rounded-card bg-card" />
          ) : product.isError || !product.data ? (
            <Card padding="lg">
              <div className="text-[14px] text-muted">{t('admin.error')}</div>
            </Card>
          ) : (
            <div className={cn('transition-opacity', product.isPlaceholderData && 'opacity-60')}>
              <AdminProductView data={product.data} metrika={metrika.data} />
            </div>
          )}

          {analytics.isPending ? (
            <div className="h-[520px] animate-skeleton rounded-card bg-card" />
          ) : analytics.isError || !analytics.data ? (
            <Card padding="lg">
              <div className="text-[14px] text-muted">{t('admin.error')}</div>
            </Card>
          ) : (
            // Смена периода: прошлые графики остаются, приглушённые, пока грузятся новые.
            <div className={cn('transition-opacity', analytics.isPlaceholderData && 'opacity-60')}>
              <AdminAnalyticsView data={analytics.data} part="product" />
            </div>
          )}

          {stories.data && (
            <div className={cn('transition-opacity', stories.isPlaceholderData && 'opacity-60')}>
              <AdminStoriesView data={stories.data} eleven={eleven.data} lang={lang} />
            </div>
          )}

        </div>
        ) : (
        <div className="space-y-8">
          {analytics.isPending ? (
            <div className="h-[520px] animate-skeleton rounded-card bg-card" />
          ) : analytics.isError || !analytics.data ? (
            <Card padding="lg">
              <div className="text-[14px] text-muted">{t('admin.error')}</div>
            </Card>
          ) : (
            // Смена периода: прошлые графики остаются, приглушённые, пока грузятся новые.
            <div className={cn('transition-opacity', analytics.isPlaceholderData && 'opacity-60')}>
              <AdminAnalyticsView data={analytics.data} part="tech" />
            </div>
          )}

          {errors.isPending ? (
            <div className="h-[420px] animate-skeleton rounded-card bg-card" />
          ) : errors.isError || !errors.data ? (
            <Card padding="lg">
              <div className="text-[14px] text-muted">{t('admin.errors.loadError')}</div>
            </Card>
          ) : (
            <div className={cn('transition-opacity', errors.isPlaceholderData && 'opacity-60')}>
              <AdminErrorsView data={errors.data} id={ERRORS_ID} />
            </div>
          )}

          <section>
            <h2 className="mb-1 font-serif text-[22px] font-medium tracking-[-0.01em]">{t('admin.title')}</h2>
            <div className="mb-3 max-w-[640px] text-[14px] leading-relaxed text-muted">
              {t('admin.sub', { days: query.data?.periodDays ?? 30 })}
            </div>
            {query.isPending ? (
              <div className="h-[320px] animate-skeleton rounded-card bg-card" />
            ) : query.isError || !query.data ? (
              <Card padding="lg">
                <div className="text-[14px] text-muted">{t('admin.error')}</div>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Оплата прошла, а период не выдан — видно только когда такое есть. */}
                {query.data.payments.unreconciledCaptures > 0 && (
                  <div className="rounded-card border border-danger/40 bg-danger/10 px-5 py-3.5 text-[14px] text-fg">
                    {t('admin.payments', {
                      n: fmtInt(query.data.payments.unreconciledCaptures),
                      sum: fmtRub(query.data.payments.unreconciledAmountRub),
                    })}
                  </div>
                )}
                <AdminSpendTable data={query.data} />
              </div>
            )}
          </section>
        </div>
        )
      )}
    </div>
  );
}
